import { Component, Inject, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  buildAdobeStyleStampImageBase64,
  buildAdobeStampDescription,
  buildUploadStampImageBase64,
  formatAdobeStampDate,
  getSignatureZoneMinimum,
  isSignatureZoneLargeEnough
} from '../../core/utils/pades-stamp-image.util';

const DEFAULT_PDF_WIDTH = 595;
const DEFAULT_PDF_HEIGHT = 842;

export interface PadesSignatureZoneModalData {
  pdfSrc: string;
  /** CN del certificado seleccionado (Certifirma Digital Virtual o P10). */
  signerCn: string;
  /**
   * RSE: el CN no se conoce hasta el OTP. Permite editar el nombre
   * para generar el mismo sello Adobe que TV/P10.
   */
  signerCnEditable?: boolean;
  initialConfig?: PadesSignatureZoneInitialConfig;
}

export interface PadesSignatureZoneResult {
  lowerLeftX: number;
  lowerLeftY: number;
  upperRightX: number;
  upperRightY: number;
  signPage: number;
}

export interface PadesSignatureImageChoice {
  useDefault: boolean;
  imageBase64?: string;
  mode?: 'default' | 'upload';
}

export type PadesSignatureTextStyle = 'normal' | 'handwritten-1' | 'handwritten-2' | 'handwritten-3';

export interface PadesVisibleSignatureConfig {
  text: string;
  style: PadesSignatureTextStyle;
}

export interface PadesSignatureZoneModalResult {
  zone: PadesSignatureZoneResult;
  imageChoice: PadesSignatureImageChoice;
  visibleSignature: PadesVisibleSignatureConfig;
}

export interface PadesSignatureZoneInitialConfig {
  zone?: PadesSignatureZoneResult | null;
  imageChoice?: PadesSignatureImageChoice | null;
  visibleSignature?: PadesVisibleSignatureConfig | null;
}

@Component({
  selector: 'app-pades-signature-zone-modal',
  templateUrl: './pades-signature-zone-modal.component.html',
  styleUrls: ['./pades-signature-zone-modal.component.css'],
  standalone: false
})
export class PadesSignatureZoneModalComponent {
  @ViewChild('overlay') overlayRef!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('pdfViewer') pdfViewerRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollContainer') scrollContainerRef!: ElementRef<HTMLDivElement>;

  pdfSrc: string;
  page = 1;
  totalPages = 0;
  /** Dimensiones en píxeles del contenido renderizado (página PDF); usadas para el overlay. */
  contentWidth = 0;
  contentHeight = 0;
  /** Dimensiones reales de la página PDF actual en puntos (obtenidas del documento). */
  pdfPageWidth = DEFAULT_PDF_WIDTH;
  pdfPageHeight = DEFAULT_PDF_HEIGHT;
  /** Referencia al documento PDF para obtener dimensiones de páginas. */
  private pdfDocument: any = null;
  /** Modo visual para la firma visible. */
  visualMode: 'default' | 'upload' = 'default';
  /** Sello por defecto con datos del certificado; cambia si el usuario sube imagen. */
  imageChoice: PadesSignatureImageChoice | null = { useDefault: true, mode: 'default' };
  selectedFile: File | null = null;
  uploadError: string | null = null;
  signerCn: string;
  readonly signerCnEditable: boolean;
  drawMode = false;
  rect: { left: number; top: number; width: number; height: number } | null = null;
  private startX = 0;
  private startY = 0;
  private isDrawing = false;
  private activePointerId: number | null = null;
  showAppearanceModal = false;
  private previewDate = new Date();
  /** Mensaje breve cuando ampliamos automáticamente un área pequeña. */
  zoneAdjustedMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PadesSignatureZoneModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PadesSignatureZoneModalData,
    private cdr: ChangeDetectorRef
  ) {
    this.pdfSrc = data.pdfSrc;
    this.signerCnEditable = !!data.signerCnEditable;
    const incomingCn = data.signerCn?.trim() || '';
    const placeholder = !incomingCn
      || incomingCn === 'Firmante'
      || incomingCn === 'Nombre del firmante';
    this.signerCn = this.signerCnEditable && placeholder ? '' : (incomingCn || 'Firmante');
    if (data.initialConfig?.visibleSignature?.text?.trim() && this.signerCnEditable) {
      this.signerCn = data.initialConfig.visibleSignature.text.trim();
    }
    this.applyInitialConfig(data.initialConfig);
  }

  onLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
    this.pdfDocument = pdf;
    this.updatePageDimensions();
    setTimeout(() => this.measureContentSize(), 100);
  }

  private updatePageDimensions(): void {
    if (!this.pdfDocument) return;
    this.pdfDocument.getPage(this.page).then((page: any) => {
      const viewport = page.getViewport({ scale: 1 });
      this.pdfPageWidth = viewport.width;
      this.pdfPageHeight = viewport.height;
      this.measureContentSize();
      this.cdr.markForCheck();
    }).catch(() => {
      this.pdfPageWidth = DEFAULT_PDF_WIDTH;
      this.pdfPageHeight = DEFAULT_PDF_HEIGHT;
    });
  }

  goToPage(p: number): void {
    this.page = p;
    this.rect = null;
    this.drawMode = false;
    this.showAppearanceModal = false;
    this.updatePageDimensions();
  }

  onPageRendered(): void {
    setTimeout(() => this.measureContentSize(), 50);
  }

  /**
   * Fija el tamaño del contenido para la conversión de coordenadas.
   * Usa las dimensiones reales del PDF para calcular la proporción correcta.
   */
  private measureContentSize(): void {
    const scrollEl = this.scrollContainerRef?.nativeElement;
    if (!scrollEl) return;
    const containerWidth = scrollEl.clientWidth;
    if (containerWidth <= 0) return;
    this.contentWidth = containerWidth;
    this.contentHeight = Math.round(containerWidth * this.pdfPageHeight / this.pdfPageWidth);
    this.cdr.markForCheck();
  }

  useDefaultStamp(): void {
    this.visualMode = 'default';
    this.imageChoice = { useDefault: true, mode: 'default' };
    this.selectedFile = null;
    this.uploadError = null;
  }

  triggerFileInput(): void {
    this.fileInputRef?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    this.uploadError = null;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      input.value = '';
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext !== 'png' && ext !== 'jpg' && ext !== 'jpeg') {
      this.uploadError = 'Use una imagen PNG o JPG.';
      this.selectedFile = null;
      this.imageChoice = null;
      input.value = '';
      return;
    }
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const b64 = dataUrl.indexOf(',') >= 0 ? dataUrl.split(',')[1] : dataUrl;
      if (b64) {
        this.visualMode = 'upload';
        this.imageChoice = { useDefault: false, imageBase64: b64, mode: 'upload' };
        this.uploadError = null;
      }
    };
    reader.onerror = () => {
      this.uploadError = 'No se pudo leer el archivo.';
      this.imageChoice = null;
    };
    reader.readAsDataURL(file);
  }

  canConfirm(): boolean {
    if (!this.hasValidRect()) {
      return false;
    }
    if (this.signerCnEditable && !this.hasValidSignerCn()) {
      return false;
    }
    return true;
  }

  hasValidSignerCn(): boolean {
    const cn = this.signerCn?.trim() || '';
    return cn.length >= 3
      && cn !== 'Firmante'
      && cn !== 'Nombre del firmante';
  }

  onSignerCnInput(value: string): void {
    this.signerCn = value ?? '';
    this.cdr.markForCheck();
  }

  get recommendedZoneMin() {
    return getSignatureZoneMinimum(this.signerCn);
  }

  isZoneLargeEnough(): boolean {
    const dims = this.getZoneDimensionsPt();
    if (!dims) return false;
    return isSignatureZoneLargeEnough(dims.widthPt, dims.heightPt, this.signerCn);
  }

  getZoneSizeWarning(): string | null {
    return this.zoneAdjustedMessage;
  }

  /** Amplía el rectángulo dibujado al tamaño mínimo recomendado, centrado. */
  private expandRectToMinimumIfNeeded(): boolean {
    if (!this.rect || this.contentWidth <= 0 || this.contentHeight <= 0) return false;
    const dims = this.getZoneDimensionsPt();
    if (!dims) return false;
    const min = this.recommendedZoneMin;
    if (dims.widthPt >= min.widthPt && dims.heightPt >= min.heightPt) {
      this.zoneAdjustedMessage = null;
      return false;
    }

    const scaleX = this.contentWidth / this.pdfPageWidth;
    const scaleY = this.contentHeight / this.pdfPageHeight;
    const minWidthPx = min.widthPt * scaleX;
    const minHeightPx = min.heightPt * scaleY;
    const centerX = this.rect.left + this.rect.width / 2;
    const centerY = this.rect.top + this.rect.height / 2;
    const newWidth = Math.max(this.rect.width, minWidthPx);
    const newHeight = Math.max(this.rect.height, minHeightPx);
    const maxLeft = Math.max(0, this.contentWidth - newWidth);
    const maxTop = Math.max(0, this.contentHeight - newHeight);

    this.rect = {
      left: Math.min(Math.max(0, centerX - newWidth / 2), maxLeft),
      top: Math.min(Math.max(0, centerY - newHeight / 2), maxTop),
      width: newWidth,
      height: newHeight
    };
    this.zoneAdjustedMessage = 'Ajustamos el área al tamaño mínimo recomendado para que la firma visible se vea bien.';
    return true;
  }

  private ensureZoneMinimumPt(zone: PadesSignatureZoneResult): PadesSignatureZoneResult {
    const min = this.recommendedZoneMin;
    const widthPt = zone.upperRightX - zone.lowerLeftX;
    const heightPt = zone.upperRightY - zone.lowerLeftY;
    if (widthPt >= min.widthPt && heightPt >= min.heightPt) {
      return zone;
    }

    const centerX = (zone.lowerLeftX + zone.upperRightX) / 2;
    const centerY = (zone.lowerLeftY + zone.upperRightY) / 2;
    const newWidth = Math.max(widthPt, min.widthPt);
    const newHeight = Math.max(heightPt, min.heightPt);

    let lowerLeftX = Math.round(centerX - newWidth / 2);
    let lowerLeftY = Math.round(centerY - newHeight / 2);
    let upperRightX = lowerLeftX + Math.round(newWidth);
    let upperRightY = lowerLeftY + Math.round(newHeight);

    if (upperRightX > this.pdfPageWidth) {
      const shift = upperRightX - this.pdfPageWidth;
      lowerLeftX -= shift;
      upperRightX -= shift;
    }
    if (upperRightY > this.pdfPageHeight) {
      const shift = upperRightY - this.pdfPageHeight;
      lowerLeftY -= shift;
      upperRightY -= shift;
    }
    lowerLeftX = Math.max(0, lowerLeftX);
    lowerLeftY = Math.max(0, lowerLeftY);

    return {
      ...zone,
      lowerLeftX,
      lowerLeftY,
      upperRightX: Math.min(this.pdfPageWidth, upperRightX),
      upperRightY: Math.min(this.pdfPageHeight, upperRightY)
    };
  }

  private getZoneDimensionsPt(): { widthPt: number; heightPt: number } | null {
    if (!this.rect || this.contentWidth <= 0 || this.contentHeight <= 0) return null;
    const scaleX = this.pdfPageWidth / this.contentWidth;
    const scaleY = this.pdfPageHeight / this.contentHeight;
    const widthPt = Math.abs(this.rect.width * scaleX);
    const heightPt = Math.abs(this.rect.height * scaleY);
    return { widthPt, heightPt };
  }

  private buildZoneFromRect(): PadesSignatureZoneResult | null {
    if (!this.rect || this.contentWidth <= 0 || this.contentHeight <= 0) return null;
    const pdfW = this.pdfPageWidth;
    const pdfH = this.pdfPageHeight;
    const scaleX = pdfW / this.contentWidth;
    const scaleY = pdfH / this.contentHeight;
    const leftPx = this.rect.left;
    const topPx = this.rect.top;
    const rightPx = leftPx + this.rect.width;
    const bottomPx = topPx + this.rect.height;
    const lowerLeftX = Math.round(leftPx * scaleX);
    const upperRightX = Math.round(rightPx * scaleX);
    const upperRightY = Math.round(pdfH - topPx * scaleY);
    const lowerLeftY = Math.round(pdfH - bottomPx * scaleY);
    return {
      lowerLeftX: Math.max(0, lowerLeftX),
      lowerLeftY: Math.max(0, lowerLeftY),
      upperRightX: Math.min(pdfW, upperRightX),
      upperRightY: Math.min(pdfH, upperRightY),
      signPage: this.page
    };
  }

  startDrawMode(): void {
    this.drawMode = true;
    this.rect = null;
    this.showAppearanceModal = false;
    this.zoneAdjustedMessage = null;
  }

  cancelDrawMode(): void {
    this.drawMode = false;
    this.rect = null;
    this.showAppearanceModal = false;
    this.zoneAdjustedMessage = null;
  }

  onOverlayPointerDown(event: PointerEvent): void {
    if (!this.drawMode || (event.isPrimary === false)) return;
    const overlayEl = this.overlayRef?.nativeElement;
    if (!overlayEl) return;
    event.preventDefault();
    const overlayRect = overlayEl.getBoundingClientRect();
    this.startX = event.clientX - overlayRect.left;
    this.startY = event.clientY - overlayRect.top;
    this.rect = { left: this.startX, top: this.startY, width: 0, height: 0 };
    this.isDrawing = true;
    this.activePointerId = event.pointerId;
    overlayEl.setPointerCapture?.(event.pointerId);
  }

  onOverlayPointerMove(event: PointerEvent): void {
    if (!this.isDrawing || !this.rect || this.activePointerId !== event.pointerId) return;
    const overlayEl = this.overlayRef?.nativeElement;
    if (!overlayEl) return;
    event.preventDefault();
    const overlayRect = overlayEl.getBoundingClientRect();
    const x = event.clientX - overlayRect.left;
    const y = event.clientY - overlayRect.top;
    this.rect = {
      left: Math.min(this.startX, x),
      top: Math.min(this.startY, y),
      width: Math.abs(x - this.startX),
      height: Math.abs(y - this.startY)
    };
  }

  onOverlayPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) return;
    const overlayEl = this.overlayRef?.nativeElement;
    overlayEl?.releasePointerCapture?.(event.pointerId);
    this.isDrawing = false;
    this.activePointerId = null;
    if (this.hasValidRect()) {
      this.expandRectToMinimumIfNeeded();
      this.openAppearanceModal();
      this.cdr.markForCheck();
    }
  }

  onOverlayPointerCancel(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) return;
    const overlayEl = this.overlayRef?.nativeElement;
    overlayEl?.releasePointerCapture?.(event.pointerId);
    this.isDrawing = false;
    this.activePointerId = null;
  }

  onOverlayPointerLeave(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) return;
    this.isDrawing = false;
  }

  openAppearanceModal(): void {
    this.previewDate = new Date();
    this.showAppearanceModal = true;
    this.drawMode = false;
  }

  closeAppearanceModal(): void {
    this.showAppearanceModal = false;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm()) return;
    const rawZone = this.buildZoneFromRect();
    if (!rawZone) return;
    const zone = this.ensureZoneMinimumPt(rawZone);

    const zoneWidthPt = Math.max(zone.upperRightX - zone.lowerLeftX, 1);
    const zoneHeightPt = Math.max(zone.upperRightY - zone.lowerLeftY, 1);
    const resolvedText = this.signerCn.trim();
    const finalImageChoice = await this.buildFinalImageChoice(zoneWidthPt, zoneHeightPt);
    this.dialogRef.close({
      zone,
      imageChoice: finalImageChoice,
      visibleSignature: {
        text: resolvedText,
        style: 'normal' as PadesSignatureTextStyle
      }
    } as PadesSignatureZoneModalResult);
  }

  hasValidRect(): boolean {
    return !!this.rect && this.rect.width >= 5 && this.rect.height >= 5;
  }

  getPreviewDateLabel(): string {
    return formatAdobeStampDate(this.previewDate);
  }

  getPreviewDescriptionText(): string {
    return buildAdobeStampDescription(this.signerCn.trim() || 'Firmante', this.previewDate);
  }

  private applyInitialConfig(config?: PadesSignatureZoneInitialConfig | null): void {
    if (!config) return;

    const mode = config.imageChoice?.mode;
    if (mode === 'upload' && config.imageChoice?.imageBase64) {
      this.visualMode = 'upload';
      this.imageChoice = {
        useDefault: false,
        mode: 'upload',
        imageBase64: config.imageChoice.imageBase64
      };
    } else {
      this.visualMode = 'default';
      this.imageChoice = { useDefault: true, mode: 'default' };
    }

    if (config.zone) {
      this.page = config.zone.signPage || 1;
    }
  }

  private async buildFinalImageChoice(zoneWidthPt: number, zoneHeightPt: number): Promise<PadesSignatureImageChoice> {
    const sizePt = { widthPt: zoneWidthPt, heightPt: zoneHeightPt };
    if (this.visualMode === 'upload' && this.imageChoice?.imageBase64) {
      const composedImage = await buildUploadStampImageBase64(
        this.imageChoice.imageBase64,
        this.previewDate,
        sizePt,
        b64 => this.loadImageFromBase64(b64)
      );
      return {
        useDefault: false,
        mode: 'upload',
        imageBase64: composedImage || this.imageChoice.imageBase64
      };
    }
    return {
      useDefault: true,
      mode: 'default',
      imageBase64: await buildAdobeStyleStampImageBase64(this.signerCn, this.previewDate, sizePt)
    };
  }

  private loadImageFromBase64(base64: string): Promise<HTMLImageElement | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      const mimeType = this.detectImageMimeType(base64);
      img.src = `data:${mimeType};base64,${base64}`;
    });
  }

  private detectImageMimeType(base64: string): string {
    if (base64.startsWith('/9j/')) return 'image/jpeg';
    if (base64.startsWith('iVBOR')) return 'image/png';
    if (base64.startsWith('R0lGOD')) return 'image/gif';
    if (base64.startsWith('UklGR')) return 'image/webp';
    return 'image/png';
  }
}
